import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// Fallback used if the API is unreachable / before the first fetch resolves.
export const DEFAULT_CONTENT = {
  hero: {
    title: "Your on-call tech geeks.",
    subtitle:
      "Pick a slot. We call you. A real technician fixes your issue over the phone — and hands you a clean case log to keep.",
    tagline: "Real support, honest pricing",
    trust_note: "Serving Dwarka, Delhi",
    primary_cta: "Book a fix · ₹599",
  },
  services: [],
  testimonials: [
    { name: "Rahul M.", city: "Mumbai", quote: "Incredibly fast and professional service. Fixed my laptop in under an hour!" },
    { name: "Priya S.", city: "Delhi", quote: "Set up my cloud backup perfectly. I finally feel my photos are safe." },
    { name: "Amit K.", city: "Bangalore", quote: "My PC was crawling and now it runs like new. Highly recommend Yonsii!" },
  ],
  about: {
    heading: "Real geeks. Real fixes.",
    mission: "Yonsii Geek Support was built for the moment you stare at your laptop and want to throw it out the window.",
    body: "We're a small crew of technicians based out of Dwarka, Delhi who believe good tech support doesn't need a call-center script. Book a slot, we call you, and we don't stop until your device works. Every session ends with a plain-English case log so you never have to explain the same problem twice.",
    values: [
      { title: "One human, one call", detail: "You always get a real technician — never a chatbot, never a tier-1 script reader." },
      { title: "Flat, honest pricing", detail: "Standard sessions are ₹599. Complex repairs start at ₹599, quoted on assessment. If we can't fix it, you don't pay." },
      { title: "Case log for everything", detail: "You keep the notes, the fix steps, and the technician's name. Forever." },
    ],
  },
  contact: {
    email: "hello@yonsiigeeksupport.com",
    whatsapp: "+91 90000 00000",
    phone: "+91 90000 00000",
    service_area: "Dwarka, Delhi",
    hours: "Mon–Sat · 9am–9pm IST",
  },
};

const SSE_URL = `${
  typeof window !== "undefined" && window.location && window.location.origin
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL || ""
}/api/events/content`;

/**
 * useSiteContent — public site content with live updates.
 * - Fetches /api/content on mount (cache-busted).
 * - Subscribes to Server-Sent Events at /api/events/content: whenever an admin
 *   saves content (e.g. a price in the Category Pricing panel), the backend
 *   broadcasts a "content-updated" event and this hook refetches automatically.
 * - Also refetches when the tab regains focus (belt-and-braces for missed pushes).
 */
export function useSiteContent() {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const aliveRef = useRef(true);

  const refetch = useCallback(async () => {
    try {
      const { data } = await api.get(`/content?_t=${Date.now()}`);
      if (aliveRef.current && data) setContent(data);
    } catch {
      // swallow — DEFAULT_CONTENT already rendered
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    refetch().finally(() => aliveRef.current && setLoading(false));

    // Live push via SSE (auto-reconnects on network drop thanks to browser behaviour)
    let es;
    try {
      es = new EventSource(SSE_URL);
      es.addEventListener("content-updated", () => refetch());
    } catch {
      // EventSource unavailable — visibility fallback still works
    }

    const onVisible = () => { if (document.visibilityState === "visible") refetch(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      aliveRef.current = false;
      if (es) es.close();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refetch]);

  return { content, loading, refetch };
}
